import React from "react";
import cn from "classnames";
import { UseFormRegister, FieldValues } from "react-hook-form";

type SelectProps = {
  /** Optional: react-hook-form supplies this via register(). */
  name?: string;
  label: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  options: Array<{ title: string; value: string | number }>;
  value?: string | number;
  onChange?: React.ChangeEventHandler<HTMLSelectElement>;
};

type Ref = HTMLSelectElement;
// Mirrors Input: the register() props are optional and onChange is ours, so this
// works both spread from register() and as a plain controlled select. Previously
// it demanded the full register() return, which made controlled use impossible -
// a caller's onChange returns void, register()'s returns Promise<boolean | void>.
type Props = SelectProps &
  Partial<Omit<ReturnType<UseFormRegister<FieldValues>>, "onChange">>;

/* Keep input names unique since ids are generated from names */
const Select = React.forwardRef<Ref, Props>(
  ({ name, label, placeholder, className, options, ...props }, ref) => {
    // Falls back to a generated id when there is no name to derive one from.
    const generatedId = React.useId();
    const id = name ? name + "__id" : generatedId;

    return (
      <div className={cn("flex flex-col gap-y-1.5", className && className)}>
        <label htmlFor={id} className="text-slate-600 text-sm font-medium">
          {label}
        </label>
        <select
          id={id}
          name={name}
          {...props}
          ref={ref}
          className="rounded-md border border-slate-300 bg-white text-sm px-3 py-2 pr-8 min-w-0 dropdown-arrow focus:outline-none focus:border-vg-blue focus:ring-[3px] focus:ring-vg-blue/15 transition-colors"
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option, idx) => (
            <option key={idx} value={option.value}>
              {option.title}
            </option>
          ))}
        </select>
      </div>
    );
  }
);

Select.displayName = "Select";

export default Select;
